import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, Send, Download, Plus, Search, Copy, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { API, getToken } from "@/lib/api";

const STATUS_STYLES = {
  Draft: "text-gray-300 bg-gray-400/10 border-gray-400/30",
  Sent: "text-blue-300 bg-blue-400/10 border-blue-400/30",
  Approved: "text-[#C9963F] bg-[#C9963F]/10 border-[#C9963F]/40",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return (
    <span data-testid={`quote-status-${status}`} className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] border ${cls}`}>
      {status}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}

export default function QuotesList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setRows(data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function sendQuote(quote) {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/send`);
      const link = `${window.location.origin}/q/${data.public_token}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Quote sent. Link copied.");
      load();
    } catch { toast.error("Failed to send quote"); }
  }

  async function copyLink(quote) {
    const link = `${window.location.origin}/q/${quote.public_token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Could not copy"); }
  }

  async function downloadPdf(quote) {
    try {
      const res = await fetch(`${API}/quotes/${quote.id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `sheesh-quote-${(quote.client_name || "client").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  }

  async function generateContract(quote) {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/generate-contract`);
      toast.success(data.already_exists ? "Contract already exists" : "Contract created from quote");
      navigate(`/admin/contracts/${data.contract_id}`);
    } catch (e) { toast.error("Failed to generate contract"); }
  }

  async function remove(quote) {
    if (!window.confirm(`Delete quote for ${quote.client_name}?`)) return;
    try { await api.delete(`/quotes/${quote.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  }

  const filtered = rows.filter((r) =>
    r.client_name?.toLowerCase().includes(q.toLowerCase()) ||
    r.status?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="quotes-list-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Pre-sales</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Quotes</h1>
          <p className="text-white/50 mt-2 text-sm">Send a quote first, generate a contract once approved.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="quotes-search"
              className="bg-[#0e0e0e] border border-white/10 pl-9 pr-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none w-56" />
          </div>
          <Link to="/admin/quotes/new" data-testid="new-quote-btn"
            className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
            <Plus size={16} /> New Quote
          </Link>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && <div className="text-white/40 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-white/40 text-sm border border-white/10 p-8 text-center">No quotes yet</div>
        )}
        {filtered.map((r) => (
          <div key={r.id} className="bg-[#0e0e0e] border border-white/10 p-4" data-testid={`quote-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-white font-medium truncate">{r.client_name}</div>
                <div className="text-xs text-white/50 mt-0.5">{fmtDate(r.event_date)}</div>
              </div>
              <StatusBadge status={r.status} />
            </div>
            <div className="text-sm text-[#C9963F] mt-2">${(r.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => navigate(`/admin/quotes/${r.id}`)} className="px-3 py-1.5 text-xs border border-white/15 hover:border-[#C9963F] hover:text-[#C9963F]"><Edit3 size={12} className="inline mr-1" /> Edit</button>
              <button onClick={() => sendQuote(r)} className="px-3 py-1.5 text-xs border border-[#C9963F]/40 text-[#C9963F]"><Send size={12} className="inline mr-1" /> Send</button>
              <button onClick={() => copyLink(r)} className="px-3 py-1.5 text-xs border border-white/15"><Copy size={12} className="inline mr-1" /> Link</button>
              <button onClick={() => downloadPdf(r)} className="px-3 py-1.5 text-xs border border-white/15"><Download size={12} className="inline mr-1" /> PDF</button>
              {r.status === "Approved" && !r.converted_contract_id && (
                <button onClick={() => generateContract(r)} className="px-3 py-1.5 text-xs bg-[#C9963F] text-black"><FileText size={12} className="inline mr-1" /> → Contract</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-[#0e0e0e] border border-white/10">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/50 border-b border-white/10">
              <th className="px-5 py-3 font-medium">Client</th>
              <th className="px-5 py-3 font-medium">Event Date</th>
              <th className="px-5 py-3 font-medium">Total</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Linked</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-white/40">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-white/40">No quotes yet</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`quote-row-${r.id}`}>
                <td className="px-5 py-4">
                  <div className="text-white font-medium">{r.client_name}</div>
                  <div className="text-xs text-white/40">{r.client_email || r.client_phone || "—"}</div>
                </td>
                <td className="px-5 py-4 text-white/70">{fmtDate(r.event_date)}</td>
                <td className="px-5 py-4 text-[#C9963F]">${(r.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
                <td className="px-5 py-4 text-xs text-white/50">
                  {r.converted_contract_id ? (
                    <Link to={`/admin/contracts/${r.converted_contract_id}`} className="text-[#C9963F] hover:underline">View contract →</Link>
                  ) : "—"}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1.5 justify-end">
                    {r.status === "Approved" && !r.converted_contract_id && (
                      <button onClick={() => generateContract(r)} title="Generate Contract" data-testid={`gen-contract-${r.id}`}
                        className="px-2.5 py-1 text-[10px] uppercase tracking-wider bg-[#C9963F] text-black hover:bg-[#A67A2D] inline-flex items-center gap-1">
                        <FileText size={11} /> Contract
                      </button>
                    )}
                    <button onClick={() => navigate(`/admin/quotes/${r.id}`)} title="Edit" data-testid={`edit-quote-${r.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F]"><Edit3 size={14} /></button>
                    <button onClick={() => sendQuote(r)} title="Send" data-testid={`send-quote-${r.id}`}
                      className="p-2 text-white/60 hover:text-[#C9963F]"><Send size={14} /></button>
                    <button onClick={() => copyLink(r)} title="Copy link"
                      className="p-2 text-white/60 hover:text-[#C9963F]"><Copy size={14} /></button>
                    <button onClick={() => downloadPdf(r)} title="PDF"
                      className="p-2 text-white/60 hover:text-[#C9963F]"><Download size={14} /></button>
                    <button onClick={() => remove(r)} title="Delete"
                      className="p-2 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
